import type {
  DecideTherapistApprovalInput,
  EndTherapistEmergencyPauseInput,
  OffTherapistDaysInput,
  ReassignTherapistSessionInput,
  RetimeTherapistSessionInput,
  SetTherapistSessionStateInput,
} from "@bednbody/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { callEdgeFunction } from "@/lib/edge-functions";
import { supabase } from "@/lib/supabase";

export interface TherapistApprovalRow {
  id: string;
  cycleId: string;
  therapistId: string;
  therapistName: string;
  weekStartDate: string | null;
  selectedDates: string[];
  reasons: string[];
  state: string;
  decisionDeadlineAt: string;
  decisionReason: string | null;
  decidedAt: string | null;
  decidedByName: string | null;
}

export interface TherapistApprovalQueue {
  /** Waiting on a decision, the soonest deadline first. */
  pending: TherapistApprovalRow[];
  /** Already decided, the newest decision first. */
  decided: TherapistApprovalRow[];
}

const APPROVAL_COLUMNS =
  "id, cycle_id, therapist_id, selected_dates, reasons, state, decision_deadline_at, submitted_at, decision_reason, decided_at, decided_by";

/**
 * The Request-for-Approval queue — TSESSION-020/022/023.
 *
 * The read is a plain `.select()` guarded by 0075's RLS, like the rider
 * equivalent: Admin sees everything, Hub Manager sees only their own hub's
 * therapists, and that scoping is the policy's job. A `.eq("hub_id", …)` here
 * would be a second implementation of it.
 *
 * ⚠ WITHOUT THIS SCREEN THE WHOLE EXCEPTION PATH IS DEAD ON ARRIVAL. Every
 * request Auto-Declines at Thursday 6:00 PM (TSESSION-023), and five TSESSION
 * rules plus a state-machine branch never execute. That is why the admin queue
 * was pulled into this program rather than deferred (D1).
 *
 * ⚠ THE THERAPIST NAME IS A SECOND QUERY, NOT AN EMBED, AND THAT IS FORCED.
 * This table has two foreign keys into `staff` — `therapist_id` and
 * `decided_by` — and the generated types map each constraint to THREE referenced
 * relations (`staff`, `v_client_therapist_display`, `v_therapist_queue`). Every
 * embed hint therefore stays ambiguous and collapses the whole row type to
 * `GenericStringError`. The cycle embed is skipped for the same reason. Three
 * typed queries beat one `as any`.
 */
export function useTherapistApprovalQueue() {
  return useQuery({
    queryKey: ["therapist-approval-queue"],
    queryFn: async (): Promise<TherapistApprovalQueue> => {
      // ⚠ TWO READS, NOT ONE. Pending and decided used to share one query
      // ordered oldest-first with a 200-row cap, so once history passed 200 the
      // rows cut off were the NEWEST — the pending requests that still needed a
      // decision, which would then Auto Decline unseen on Thursday.
      const [pendingRes, decidedRes] = await Promise.all([
        supabase
          .from("therapist_session_approval_requests")
          .select(APPROVAL_COLUMNS)
          .eq("state", "pending")
          .order("decision_deadline_at", { ascending: true })
          .limit(500),
        supabase
          .from("therapist_session_approval_requests")
          .select(APPROVAL_COLUMNS)
          .neq("state", "pending")
          .order("decided_at", { ascending: false, nullsFirst: false })
          .order("submitted_at", { ascending: false })
          .limit(100),
      ]);
      if (pendingRes.error) throw pendingRes.error;
      if (decidedRes.error) throw decidedRes.error;

      const all = [...(pendingRes.data ?? []), ...(decidedRes.data ?? [])];

      const staffIds = [
        ...new Set(
          all.flatMap((r) => (r.decided_by ? [r.therapist_id, r.decided_by] : [r.therapist_id])),
        ),
      ];
      const names = new Map<string, string>();
      if (staffIds.length > 0) {
        const { data: staff, error: staffError } = await supabase
          .from("staff")
          .select("id, full_name")
          .in("id", staffIds);
        if (staffError) throw staffError;
        for (const s of staff ?? []) names.set(s.id, s.full_name);
      }

      const cycleIds = [...new Set(all.map((r) => r.cycle_id))];
      const weeks = new Map<string, string>();
      if (cycleIds.length > 0) {
        const { data: cycles, error: cycleError } = await supabase
          .from("therapist_session_cycles")
          .select("id, week_start_date")
          .in("id", cycleIds);
        if (cycleError) throw cycleError;
        for (const c of cycles ?? []) weeks.set(c.id, c.week_start_date);
      }

      const toRow = (r: (typeof all)[number]): TherapistApprovalRow => ({
        id: r.id,
        cycleId: r.cycle_id,
        therapistId: r.therapist_id,
        // A therapist the reader may not resolve shows as unnamed rather than as
        // a uuid — RLS decides who they can see, and an id is not a person.
        therapistName: names.get(r.therapist_id) ?? "—",
        weekStartDate: weeks.get(r.cycle_id) ?? null,
        selectedDates: r.selected_dates ?? [],
        reasons: r.reasons ?? [],
        state: r.state,
        decisionDeadlineAt: r.decision_deadline_at,
        decisionReason: r.decision_reason,
        decidedAt: r.decided_at,
        // No decider on an Auto Decline or a withdrawal — the system did it.
        decidedByName: r.decided_by ? (names.get(r.decided_by) ?? "—") : null,
      });

      return {
        pending: (pendingRes.data ?? []).map(toRow),
        decided: (decidedRes.data ?? []).map(toRow),
      };
    },
    // The rolling 6:00 PM deadline moves daily and the Thursday sweep runs
    // hourly, so a stale queue can show a request that is already Auto Declined.
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

/**
 * TSESSION-022 — the decision.
 *
 * ⚠ THE INPUT CARRIES NO DATES, AND THAT ABSENCE IS THE RULE: "Approval secures
 * exactly the dates stored in the request. Admin must not add or replace dates
 * automatically." The Edge Function reads the stored proposal and commits it in
 * the same transaction as the decision. If a `dates` field ever appears in this
 * call, an approver has been given the power to commit a therapist to a week she
 * did not choose.
 */
export function useDecideTherapistApproval() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: DecideTherapistApprovalInput) =>
      callEdgeFunction("therapist-sessions/approval/decide", { data: input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["therapist-approval-queue"] });
    },
  });
}

// ── today's operational state (0085) ────────────────────────────────────────

export interface TherapistSessionTodayRow {
  id: string;
  /** Needed by the multi-date removal (0197), which is keyed on the cycle. */
  cycleId: string;
  therapistId: string;
  serviceDate: string;
  therapistName: string;
  hubId: string;
  hubName: string;
  state: string;
  startsAt: string;
  endsAt: string;
  startedAt: string | null;
  /** `emergency_paused` while she is on an Emergency Pause (TEND-061). */
  dailyState: string | null;
}

/**
 * Who is on duty today — the read that did not exist before Start Session did.
 *
 * ⚠ WHY THIS SCREEN EXISTS AT ALL. Once dispatch depends on Session state, "why
 * is nobody available in Cainta?" becomes a question ops cannot answer: the
 * approval queue shows next week's paperwork, and `pick_therapists` is a
 * function with no UI. This is the only place a hub manager can see that four
 * therapists secured today and none of them tapped Start.
 *
 * Scoping is the POLICY's job, exactly as the approval queue above explains:
 * Admin sees every hub, Hub Manager sees their own. No `.eq("hub_id", …)` here.
 *
 * The therapist name is a second query for the same reason the queue documents —
 * two foreign keys into `staff` make every embed hint ambiguous.
 */
export function useTherapistSessionsToday(serviceDate: string, enabled = true) {
  return useQuery({
    queryKey: ["therapist-sessions-today", serviceDate],
    enabled,
    queryFn: async (): Promise<TherapistSessionTodayRow[]> => {
      const { data, error } = await supabase
        .from("therapist_sessions")
        .select(
          "id, cycle_id, therapist_id, service_date, hub_id, state, starts_at, ends_at, started_at, daily_state",
        )
        .eq("service_date", serviceDate)
        .order("started_at", { ascending: true, nullsFirst: false })
        .limit(500);
      if (error) throw error;
      const rows = data ?? [];
      if (rows.length === 0) return [];

      const [staffRes, hubRes] = await Promise.all([
        supabase
          .from("staff")
          .select("id, full_name")
          .in("id", [...new Set(rows.map((r) => r.therapist_id))]),
        supabase
          .from("hubs")
          .select("id, name")
          .in("id", [...new Set(rows.map((r) => r.hub_id))]),
      ]);
      if (staffRes.error) throw staffRes.error;
      if (hubRes.error) throw hubRes.error;

      const names = new Map((staffRes.data ?? []).map((s) => [s.id, s.full_name]));
      const hubs = new Map((hubRes.data ?? []).map((h) => [h.id, h.name]));

      return rows.map((r) => ({
        id: r.id,
        cycleId: r.cycle_id,
        therapistId: r.therapist_id,
        serviceDate: r.service_date,
        therapistName: names.get(r.therapist_id) ?? "—",
        hubId: r.hub_id,
        hubName: hubs.get(r.hub_id) ?? "—",
        state: r.state,
        startsAt: r.starts_at,
        endsAt: r.ends_at,
        startedAt: r.started_at,
        dailyState: r.daily_state,
      }));
    },
    // Matches the approval queue's cadence. The 2:00 PM flip is derived from the
    // clock, so a poll is all that is needed to see Waiting become Available.
    refetchInterval: 30_000,
    staleTime: 15_000,
  });
}

// ── manual Session overrides — OQ-122 ───────────────────────────────────────
//
// ⚠ CORE-05A §7.4 says "Visibility does not create authority to bypass the
// approved automated logic", and the Role Matrix gives Admin one verb here
// ("Reviews defined exceptions"). The client overruled both (DECISIONS.md
// OQ-122, relayed by Leon 2026-09-04); 0195 carries the argument.

/**
 * Therapists holding an Off Session on this date and Dispatch Area — the only
 * people who can be handed the date.
 *
 * ⚠ THIS IS NOT A UI CONVENIENCE FILTER. `admin_reassign_therapist_session`
 * REQUIRES the incoming therapist to already hold an `off_final` row on the
 * exact date and hub (TSESSION-037's predicate, the same one the Trade accept
 * uses) — it is what guarantees a row exists to flip. Offering anyone else would
 * be offering a choice the server refuses.
 */
export function useOffTherapistsForDate(serviceDate: string, hubId: string | null) {
  return useQuery({
    queryKey: ["off-therapists", serviceDate, hubId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("therapist_sessions")
        .select("id, therapist_id")
        .eq("service_date", serviceDate)
        .eq("hub_id", hubId!)
        .eq("state", "off_final")
        .is("started_at", null);
      if (error) throw error;
      const rows = data ?? [];
      if (rows.length === 0) return [];

      // Second query, not an embed — the approval queue above documents why:
      // two foreign keys into `staff` make every embed hint ambiguous and
      // collapse the row type.
      const { data: staff, error: staffError } = await supabase
        .from("staff")
        .select("id, full_name, therapist_rank, active")
        .in("id", [...new Set(rows.map((r) => r.therapist_id))])
        .eq("active", true);
      if (staffError) throw staffError;
      const byId = new Map((staff ?? []).map((s) => [s.id, s]));

      return rows
        .map((r) => ({
          sessionId: r.id,
          therapistId: r.therapist_id,
          staff: byId.get(r.therapist_id),
        }))
        .filter((r) => r.staff !== undefined)
        .map((r) => ({
          sessionId: r.sessionId,
          therapistId: r.therapistId,
          fullName: r.staff!.full_name,
          rank: r.staff!.therapist_rank,
        }))
        .sort((a, b) => a.fullName.localeCompare(b.fullName));
    },
    enabled: hubId !== null,
    staleTime: 30_000,
  });
}

export type OffTherapist = NonNullable<ReturnType<typeof useOffTherapistsForDate>["data"]>[number];

/**
 * Everything a Session override changes.
 *
 * ⚠ THE CYCLE WEEK IS HERE FOR EVERY OVERRIDE, not only the off-days one. The
 * Move dialog reads her week to decide which dates are selectable; a state
 * change or a hand-over left it cached for 15 s, so reopening Move could offer a
 * date in its old state and the all-or-nothing off-days call then failed on it.
 * Attendance and the dispatch therapist queue read the same rows.
 */
function invalidateTherapistSessions(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ["therapist-sessions-today"] });
  void queryClient.invalidateQueries({ queryKey: ["off-therapists"] });
  void queryClient.invalidateQueries({ queryKey: ["therapist-cycle-week"] });
  void queryClient.invalidateQueries({ queryKey: ["attendance"] });
  void queryClient.invalidateQueries({ queryKey: ["therapist-queue"] });
}

/**
 * Put a Therapist on a date she was Off, or take her off one she holds.
 *
 * ⚠ ONE HOOK FOR BOTH, because it is one write. 0074 already created her row for
 * every date of the cycle — Off dates included, as first-class objects — so
 * "add" and "remove" are the same `state` moving in opposite directions. There
 * is nothing to create and nothing to delete.
 */
export function useSetTherapistSessionState() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sessionId: string } & SetTherapistSessionStateInput) => {
      const { sessionId, ...body } = input;
      return callEdgeFunction(`admin/therapist-sessions/${sessionId}/state`, { data: body });
    },
    onSuccess: () => invalidateTherapistSessions(queryClient),
  });
}

/** 0352 — Superadmin ends her Emergency Pause early (TEND-061). */
export function useEndTherapistEmergencyPause() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sessionId: string } & EndTherapistEmergencyPauseInput) => {
      const { sessionId, ...body } = input;
      return callEdgeFunction(`admin/therapist-sessions/${sessionId}/end-emergency-pause`, {
        data: body,
      });
    },
    onSuccess: () => invalidateTherapistSessions(queryClient),
  });
}

/**
 * Her whole cycle week, so the removal dialog can offer "this day AND these".
 *
 * ⚠ IT RETURNS EVERY DATE, INCLUDING THE ONES THAT CANNOT BE REMOVED. The dialog
 * shows a started or closed date as disabled rather than hiding it, because 0197
 * is ALL-OR-NOTHING: if the operator cannot see that Friday is already under way,
 * a selection including it fails as a whole and the refusal looks arbitrary.
 * Showing it greyed out with the reason is the difference between a rule and a
 * bug.
 */
export function useTherapistCycleWeek(cycleId: string | null, therapistId: string | null) {
  return useQuery({
    queryKey: ["therapist-cycle-week", cycleId, therapistId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("therapist_sessions")
        .select("id, service_date, state, started_at, ends_at")
        .eq("cycle_id", cycleId!)
        .eq("therapist_id", therapistId!)
        .order("service_date");
      if (error) throw error;
      return data ?? [];
    },
    enabled: cycleId !== null && therapistId !== null,
    staleTime: 15_000,
  });
}

export type TherapistCycleDay = NonNullable<
  ReturnType<typeof useTherapistCycleWeek>["data"]
>[number];

/**
 * Take her off several dates in one all-or-nothing call (0197).
 *
 * ⚠ NOT A LOOP OVER `useSetTherapistSessionState`. Looping would be N
 * transactions and N audit rows for one decision, and a failure partway leaves
 * the week half-changed — the operator believes she is off, the schedule says
 * she is off three days of it, and nothing surfaces the difference until a
 * booking is allocated to her.
 */
export function useOffTherapistDays() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: OffTherapistDaysInput) =>
      callEdgeFunction("admin/therapist-sessions/off-days", { data: input }),
    onSuccess: () => invalidateTherapistSessions(queryClient),
  });
}

/**
 * Hand one Secured date to a Therapist who is Off it — the two-row flip.
 *
 * A separate endpoint rather than two `useSetTherapistSessionState` calls
 * because the pair must be atomic: two calls leave a window where the date is
 * held by nobody, and a failure on the second silently drops that date's supply.
 */
export function useReassignTherapistSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sessionId: string } & ReassignTherapistSessionInput) => {
      const { sessionId, ...body } = input;
      return callEdgeFunction(`admin/therapist-sessions/${sessionId}/reassign`, { data: body });
    },
    onSuccess: () => invalidateTherapistSessions(queryClient),
  });
}

/**
 * Move a not-yet-started, finalized Secured date's starts_at/ends_at within
 * its own service_date (0245).
 *
 * ⚠ SUPERADMIN ONLY, NARROWER THAN THE REST OF THIS FILE'S OQ-122 FAMILY. The
 * Edge Function route enforces it — an Admin calling this gets a 403, unlike
 * every other mutation above. CORE-05B §4.9.4 explicitly excludes "time" from
 * OQ-122's authority (0193/0195's header), so this is a separate, narrower
 * grant, not an extension of it.
 */
export function useRetimeTherapistSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { sessionId: string } & RetimeTherapistSessionInput) => {
      const { sessionId, ...body } = input;
      return callEdgeFunction(`admin/therapist-sessions/${sessionId}/retime`, { data: body });
    },
    onSuccess: () => invalidateTherapistSessions(queryClient),
  });
}

// ── the cycle itself (0242) ────────────────────────────────────────────────

export interface TherapistCycleStatus {
  id: string;
  week_start_date: string;
  status: "not_open" | "priority_open" | "regular_open" | "securing_closed" | "finalized";
  rules_version: string;
  priority_opens_at: string;
  regular_opens_at: string;
  approval_cutoff_at: string;
  countdown_starts_at: string;
  securing_cutoff_at: string;
  finalized_at: string | null;
  created_at: string;
  members: number;
  secured: number;
  sessions: number;
}

/**
 * Cycle state, newest week first.
 *
 * ⚠ Read through the Edge Function, not a `.select()` on the view. 0242 grants
 * `v_therapist_cycle_status` to `admin` and `superadmin` as Postgres roles, and
 * an admin's PostgREST execution role only becomes one of those above aal2 —
 * routing it through the function keeps the read on one path with the three
 * writes beside it instead of depending on that.
 */
export function useTherapistCycles(limit = 5) {
  return useQuery({
    queryKey: ["therapist-cycles", limit],
    queryFn: async () =>
      callEdgeFunction<{ items: TherapistCycleStatus[] }>(`admin/therapist-cycles?limit=${limit}`, {
        method: "GET",
      }),
    select: (d) => d.items,
  });
}

export type TherapistCycleStep = "create" | "open-priority" | "open-regular";

export interface RunCycleStepResult {
  /** create only — false when the week already existed and only membership refreshed. */
  created?: boolean;
  weekStartDate?: string;
  /** open only — how many cycles actually moved. 0 means the window has not arrived. */
  opened?: number;
}

/**
 * Run by hand what `pg_cron` normally runs.
 *
 * ⚠ The result is not a success/failure flag. `create` is idempotent on the
 * service week and both openers are time-gated in SQL, so a press can
 * legitimately change nothing — the caller must read `created` / `opened` and
 * say so, rather than reporting every 200 as "done".
 */
export function useRunTherapistCycleStep() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ step, reason }: { step: TherapistCycleStep; reason: string }) =>
      callEdgeFunction<RunCycleStepResult>(
        step === "create" ? "admin/therapist-cycles" : `admin/therapist-cycles/${step}`,
        { data: { reason } },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["therapist-cycles"] });
      invalidateTherapistSessions(queryClient);
    },
  });
}
